/**
 * 领域枚举 —— 契约 §1，全栈唯一真源。
 *
 * 这里刻意不引入任何运行时依赖（无 zod、无 enum），原因有二：
 * 1) apps/mobile 编译到微信小程序，包体与冷启动敏感，纯 const 数组可被 tree-shake；
 * 2) `as const` 推导出的字面量联合类型能同时供 TS 类型与 zod 的 z.enum() 使用，
 *    避免"类型一份、校验规则又一份"的双真源漂移。
 */

export const DOMAINS = ['family_education', 'psychology', 'health', 'legal', 'encyclopedia'] as const;
export type Domain = (typeof DOMAINS)[number];

/** 家庭教育年龄段分层 */
export const AGE_BANDS = ['0-3', '4-6', '7-12', '13-18'] as const;
export type AgeBand = (typeof AGE_BANDS)[number];

/** 领域安全等级：决定审核严格度与拒答话术强度 */
export type DomainSafetyLevel = 'low' | 'medium' | 'high' | 'critical';

export interface DomainMeta {
  /** 中文名，前端领域标签直接展示 */
  readonly label: string;
  /** 是否需要年龄段；family_education 缺失时由路由推断，兜底 '7-12' */
  readonly requiresAgeBand: boolean;
  /** 年龄段缺失时的兜底值；不要求年龄段的领域为 null */
  readonly fallbackAgeBand: AgeBand | null;
  /** 安全等级（契约 §1 表格"安全等级"列） */
  readonly safetyLevel: DomainSafetyLevel;
  /** 安全等级的中文说明，用于架构文档与审计后台展示 */
  readonly safetyNote: string;
}

/**
 * 契约 §1 表格的代码化。
 * 用 Record<Domain, DomainMeta> 而非 Partial<>，保证新增领域时编译器强制补全元数据。
 */
export const DOMAIN_META: Readonly<Record<Domain, DomainMeta>> = Object.freeze({
  family_education: {
    label: '家庭教育',
    requiresAgeBand: true,
    fallbackAgeBand: '7-12',
    safetyLevel: 'high',
    safetyNote: '高（涉未成年人）',
  },
  psychology: {
    label: '心理健康',
    requiresAgeBand: false,
    fallbackAgeBand: null,
    safetyLevel: 'critical',
    safetyNote: '极高（自伤/自杀风险词强制拦截→转介）',
  },
  health: {
    label: '健康科普',
    requiresAgeBand: false,
    fallbackAgeBand: null,
    safetyLevel: 'high',
    safetyNote: '高（禁止诊断/处方）',
  },
  legal: {
    label: '法律咨询',
    requiresAgeBand: false,
    fallbackAgeBand: null,
    safetyLevel: 'medium',
    safetyNote: '中（声明非正式法律意见）',
  },
  encyclopedia: {
    label: '百科问答',
    requiresAgeBand: false,
    fallbackAgeBand: null,
    safetyLevel: 'low',
    safetyNote: '低',
  },
});

/**
 * 领域路由的兜底领域。
 * 契约/Lead Directives §5.3 明确：兜底绝不可为 psychology，
 * 否则一次误判就会把普通问题送进危机干预流程。
 */
export const FALLBACK_DOMAIN: Domain = 'encyclopedia';

/** 家庭教育的年龄段兜底值（契约 §1） */
export const FAMILY_EDUCATION_FALLBACK_AGE_BAND: AgeBand = '7-12';

/** 领域路由来源（契约 §3.3 POST /chat/route 的 source 字段） */
export const ROUTE_SOURCES = ['rule', 'cache', 'llm', 'fallback'] as const;
export type RouteSource = (typeof ROUTE_SOURCES)[number];

/**
 * 类型守卫：入参声明为 unknown 而非 string，
 * 因为调用方拿到的通常是 HTTP body / LLM 返回值，未经校验。
 */
export function isDomain(value: unknown): value is Domain {
  return typeof value === 'string' && (DOMAINS as readonly string[]).includes(value);
}

export function isAgeBand(value: unknown): value is AgeBand {
  return typeof value === 'string' && (AGE_BANDS as readonly string[]).includes(value);
}

export function isRouteSource(value: unknown): value is RouteSource {
  return typeof value === 'string' && (ROUTE_SOURCES as readonly string[]).includes(value);
}

/** 取领域中文名；未知领域回退到字符串本身，避免前端渲染 undefined */
export function domainLabel(domain: Domain): string {
  return DOMAIN_META[domain].label;
}

/**
 * 从自由文本中推断家庭教育年龄段（Lead Directives §5.3）。
 * 返回 null 表示未命中，交由调用方继续走 Profile.childAgeBands[0] → 兜底 '7-12'。
 *
 * 用"取第一个命中"而非"取最具体"：中文语境里用户先说的通常就是主语（"孩子5岁"），
 * 后文出现的年龄多半是旁枝（"我自己35岁"）。
 */
export function inferAgeBandFromText(text: string): AgeBand | null {
  // 形式一：阿拉伯数字 + 岁（"5岁"、"13 岁"）
  const digitMatch = /(\d{1,2})\s*岁/.exec(text);
  if (digitMatch?.[1]) {
    const band = bandFromAge(Number.parseInt(digitMatch[1], 10));
    if (band) return band;
  }

  // 形式二：汉字数字 + 岁（"五岁"、"十三岁"、"二十三岁"）
  const cnMatch = /([零一二三四五六七八九十两]{1,3})\s*岁/.exec(text);
  if (cnMatch?.[1]) {
    const age = parseChineseNumber(cnMatch[1]);
    if (age !== null) {
      const band = bandFromAge(age);
      if (band) return band;
    }
  }

  // 形式三：年级（"三年级" → 7-12；"初一/高二" → 13-18）
  const gradeMatch = /([一二三四五六七八九十\d]{1,2})\s*年级/.exec(text);
  if (gradeMatch?.[1]) {
    const raw = gradeMatch[1];
    const grade = /^\d+$/.test(raw) ? Number.parseInt(raw, 10) : parseChineseNumber(raw);
    if (grade !== null) {
      if (grade >= 1 && grade <= 6) return '7-12';
      // 初一~初三 / 高一~高三 落进 13-18
      if (grade <= 12) return '13-18';
    }
  }

  // 形式四：学段直陈（"上初中"、"高中生"）
  if (/(初中|高中生|高中|中考|高考)/.test(text)) return '13-18';
  if (/(幼儿园|学前|托班)/.test(text)) return '4-6';

  return null;
}

function bandFromAge(age: number): AgeBand | null {
  if (!Number.isInteger(age) || age < 0 || age > 18) return null;
  if (age <= 3) return '0-3';
  if (age <= 6) return '4-6';
  if (age <= 12) return '7-12';
  return '13-18';
}

/**
 * 只解析 0-99 这个窄区间，覆盖不了"廿/百"等字，
 * 但年龄/年级场景下够用——为不存在的输入写大数解析是没有收益的复杂度。
 * 解析失败返回 null（而不是 NaN/0），由调用方决定是否继续尝试下一种形式。
 */
function parseChineseNumber(raw: string): number | null {
  const DIGITS: Readonly<Record<string, number>> = {
    零: 0,
    一: 1,
    二: 2,
    两: 2,
    三: 3,
    四: 4,
    五: 5,
    六: 6,
    七: 7,
    八: 8,
    九: 9,
  };

  if (raw.length === 0) return null;
  if (raw === '十') return 10;

  const tenIndex = raw.indexOf('十');
  if (tenIndex === -1) {
    const value = DIGITS[raw];
    return value === undefined ? null : value;
  }

  const tensPart = raw.slice(0, tenIndex);
  const onesPart = raw.slice(tenIndex + 1);
  const tens = tensPart === '' ? 1 : DIGITS[tensPart];
  const ones = onesPart === '' ? 0 : DIGITS[onesPart];
  if (tens === undefined || ones === undefined) return null;
  return tens * 10 + ones;
}

/* ------------------------------------------------------------------ *
 * 身份（用户裁决 2026-10-10）
 *
 * 三级身份写在领域层而不是数据库层，理由与 Domain 相同：三端（小程序/Web/移动端）
 * 都要按 role 决定"显示哪个外壳"，若只把字符串散落在各处，迟早出现
 * 「服务端加了第四种角色，前端把它当普通用户放行」这种静默越权。
 * ------------------------------------------------------------------ */

export const USER_ROLES = ['user', 'admin', 'root'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const ROLE_LABEL: Readonly<Record<UserRole, string>> = Object.freeze({
  user: '普通用户',
  admin: '管理员',
  root: 'ROOT',
});

export function isUserRole(value: unknown): value is UserRole {
  return typeof value === 'string' && (USER_ROLES as readonly string[]).includes(value);
}

export function roleLabel(role: UserRole): string {
  return ROLE_LABEL[role];
}

/**
 * 封号两种形态。
 *
 * 刻意不把「短期/长期」做成两个数据库枚举值：它们是**同一个状态**（`status='suspended'`）
 * 的两种到期语义，用 `suspendedUntil` 是否为 null 区分即可。若做成两个枚举值，
 * 「短期封号到期」就必须有一个定时任务去改值——而定时任务是这个单进程服务里
 * 最不可靠的一环。现在改成惰性判定：读取时算一次，过期即视为可用。
 */
export const SUSPENSION_KINDS = ['temporary', 'permanent'] as const;
export type SuspensionKind = (typeof SUSPENSION_KINDS)[number];

export const SUSPENSION_KIND_LABEL: Readonly<Record<SuspensionKind, string>> = Object.freeze({
  temporary: '短期封号',
  permanent: '长期封号',
});

export function isSuspensionKind(value: unknown): value is SuspensionKind {
  return typeof value === 'string' && (SUSPENSION_KINDS as readonly string[]).includes(value);
}

/** 短期封号上限 365 天；再长就应该用长期封号，避免"事实上的永封"被记成临时。 */
export const SUSPENSION_MAX_HOURS = 8760;

/**
 * 生效状态判定：只有"未到期的封号"才算被封。
 *
 * 入参 `status` 声明成 string 而不是 dto.ts 的 `UserStatus`，是为了让本文件
 * 保持零依赖——dto.ts 反向 import 本文件，这里再 import 回去会形成运行时循环。
 */
export function isSuspensionActive(status: string, suspendedUntil: Date | null, now: Date): boolean {
  if (status !== 'suspended') return false;
  if (suspendedUntil === null) return true;
  return suspendedUntil.getTime() > now.getTime();
}

/* ------------------------------------------------------------------ *
 * LLM 渠道（用户裁决 2026-10-10：渠道要落库并真正接入 AI 网关）
 * ------------------------------------------------------------------ */

export const LLM_PROVIDERS = ['openai_compatible'] as const;
export type LlmProvider = (typeof LLM_PROVIDERS)[number];

export const LLM_PROVIDER_LABEL: Readonly<Record<LlmProvider, string>> = Object.freeze({
  openai_compatible: 'OpenAI 兼容',
});

export function isLlmProvider(value: unknown): value is LlmProvider {
  return typeof value === 'string' && (LLM_PROVIDERS as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------ *
 * 邮箱发送渠道（v1.5.0：与 LLM 渠道同构——表里配，env 兜底）
 * ------------------------------------------------------------------ */

/**
 * 邮件服务商的**协议**类型（不是厂商名）。
 *
 * 只有 `http_json` 一种：把密钥放在 `Authorization: Bearer`、`POST {baseUrl}/emails`、
 * body 为 `{ from, to, subject, text }` 的 JSON 接口。Resend 原生就是这个形状，
 * 任何自建/中转网关只要对齐这三个要素也能接。
 *
 * 为什么不做 SMTP：SMTP 需要自己实现 TLS 握手、AUTH LOGIN 与 MIME 组装，
 * 是几百行手写协议且极难测；而本仓的门禁要求零新增运行时依赖（CI 不联网装包）。
 * 保持封闭集合，是为了让"新增一种发信协议"必须同时改
 * apps/server/src/email/email-sender.service.ts 的 buildRequest/解析器与这里，
 * 而不是让运营在后台填一个没人实现的 provider 然后整条链路静默失败。
 */
export const EMAIL_PROVIDERS = ['http_json'] as const;
export type EmailProvider = (typeof EMAIL_PROVIDERS)[number];

export const EMAIL_PROVIDER_LABEL: Readonly<Record<EmailProvider, string>> = Object.freeze({
  http_json: 'HTTP JSON 接口（Resend 兼容）',
});

export function isEmailProvider(value: unknown): value is EmailProvider {
  return typeof value === 'string' && (EMAIL_PROVIDERS as readonly string[]).includes(value);
}
